import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Play, ArrowLeft, Crown, Clock, Film, Library, 
  Calendar, Check, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import VODFooter from '../components/VODFooter';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VODMyLibrary = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [purchasedVideos, setPurchasedVideos] = useState([]);
  const [watchHistory, setWatchHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('purchased');

  useEffect(() => {
    const token = localStorage.getItem('livestreamToken');
    if (!token) {
      navigate('/live/login?from=vod&redirect=/vod/my-library');
      return;
    }
    fetchLibrary();
  }, [navigate]);

  const fetchLibrary = async () => {
    try {
      const token = localStorage.getItem('livestreamToken');
      const res = await axios.get(`${API}/api/vod/my-library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscription(res.data.subscription);
      setPurchasedVideos(res.data.purchased_videos || []);
      setWatchHistory(res.data.watch_history || []);
    } catch (err) {
      console.error('Error fetching library:', err);
      if (err.response?.status === 401) {
        navigate('/live/login?from=vod&redirect=/vod/my-library');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="my-library-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/vod')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/vod')}>
            <img 
              src="/ltv-icon.png" 
              alt="LTV Logo" 
              className="w-8 h-8 object-contain brightness-0 invert"
            />
            <span className="font-bold">LTV VOD</span>
          </div>
          
          <div className="flex-1" />
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/vod/browse')}
            className="border-white/20"
          >
            Browse More
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-8" data-testid="my-library-header">
          <Library className="h-8 w-8 text-purple-400" />
          <h1 className="text-3xl font-bold" data-testid="my-library-title">My Library</h1>
        </div>

        {/* Subscription Status */}
        {subscription ? (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-purple-900/50 to-pink-900/50 border border-purple-500/50">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="h-6 w-6 text-yellow-400" />
                  <h2 className="text-xl font-bold capitalize">{subscription.tier} Subscription</h2>
                  <Badge className="bg-green-600">Active</Badge>
                </div>
                <p className="text-gray-400 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Renews on {new Date(subscription.expires_at).toLocaleDateString()}
                </p>
              </div>
              <Button
                onClick={() => navigate('/vod/membership')}
                variant="outline"
                className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
              >
                Manage Subscription
              </Button>
            </div>
          </div>
        ) : (
          <div className="mb-8 p-6 rounded-2xl bg-white/5 border border-white/10">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                  No Active Subscription
                </h2>
                <p className="text-gray-400">
                  Subscribe to get unlimited access to our entire content library
                </p>
              </div>
              <Button
                onClick={() => navigate('/vod/membership')}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
              >
                <Crown className="h-4 w-4 mr-2" />
                View Plans
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10" data-testid="library-tabs">
          <button
            onClick={() => setActiveTab('purchased')}
            data-testid="purchased-tab"
            className={`pb-4 px-2 font-medium transition-colors relative ${
              activeTab === 'purchased' 
                ? 'text-purple-400' 
                : 'text-gray-500 hover:text-white'
            }`}
          >
            Purchased Videos
            {purchasedVideos.length > 0 && (
              <Badge className="ml-2 bg-purple-600 text-xs">{purchasedVideos.length}</Badge>
            )}
            {activeTab === 'purchased' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            data-testid="watch-history-tab"
            className={`pb-4 px-2 font-medium transition-colors relative ${
              activeTab === 'history' 
                ? 'text-purple-400' 
                : 'text-gray-500 hover:text-white'
            }`}
          >
            Watch History
            {activeTab === 'history' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500" />
            )}
          </button>
        </div>

        {/* Content */}
        {activeTab === 'purchased' ? (
          purchasedVideos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {purchasedVideos.map((video) => (
                <VideoCard 
                  key={video.id} 
                  video={video} 
                  onClick={() => navigate(`/vod/watch/${video.id}`)}
                  owned={true}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Film className="h-16 w-16 mx-auto text-gray-700 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Purchased Videos</h3>
              <p className="text-gray-500 mb-6">Videos you purchase will appear here</p>
              <Button
                onClick={() => navigate('/vod/browse')}
                className="bg-purple-600 hover:bg-purple-500"
              >
                Browse Videos
              </Button>
            </div>
          )
        ) : (
          watchHistory.length > 0 ? (
            <div className="space-y-4" data-testid="watch-history-list">
              {watchHistory.map((item, index) => (
                <div 
                  key={index}
                  data-testid={`watch-history-item-${item.video_id}`}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                  onClick={() => navigate(`/vod/watch/${item.video_id}`)}
                >
                  <div className="w-32 aspect-video rounded-lg overflow-hidden bg-gray-900 flex-shrink-0">
                    {item.thumbnail_url ? (
                      <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" data-testid="watch-history-thumbnail" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="h-8 w-8 text-white/30" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate" data-testid="watch-history-title">{item.video_title || 'Unknown Video'}</h3>
                    <p className="text-sm text-gray-500" data-testid="watch-history-date">
                      Watched {new Date(item.watched_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-500" data-testid="watch-history-continue-btn">
                    <Play className="h-4 w-4 mr-1" /> Continue
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20" data-testid="watch-history-empty">
              <Clock className="h-16 w-16 mx-auto text-gray-700 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Watch History</h3>
              <p className="text-gray-500 mb-6">Start watching videos to build your history</p>
              <Button
                onClick={() => navigate('/vod/browse')}
                className="bg-purple-600 hover:bg-purple-500"
              >
                Browse Videos
              </Button>
            </div>
          )
        )}
      </main>
      
      {/* Footer */}
      <VODFooter />
    </div>
  );
};

// Video Card Component
const VideoCard = ({ video, onClick, owned }) => {
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) return `${hrs}h ${mins % 60}m`;
    return `${mins}m`;
  };

  return (
    <div onClick={onClick} className="group cursor-pointer">
      <div className="relative aspect-video rounded-xl overflow-hidden bg-gray-900 mb-2">
        {video.thumbnail_url ? (
          <img 
            src={video.thumbnail_url}
            alt={video.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-pink-900/50">
            <Play className="h-12 w-12 text-white/30" />
          </div>
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        
        {/* Play button on hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-purple-600/90 flex items-center justify-center">
            <Play className="h-6 w-6 text-white ml-1" fill="white" />
          </div>
        </div>
        
        {/* Owned badge */}
        {owned && (
          <Badge className="absolute top-2 right-2 bg-green-600 text-xs">
            <Check className="h-3 w-3 mr-1" /> Owned
          </Badge>
        )}
        
        {/* Tier badge */}
        {video.tier === 'premium' && (
          <Badge className="absolute top-2 left-2 bg-gradient-to-r from-purple-500 to-pink-500 text-xs">
            <Crown className="h-3 w-3 mr-1" /> Premium
          </Badge>
        )}
        
        {/* Duration */}
        {video.duration > 0 && (
          <span className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
            {formatDuration(video.duration)}
          </span>
        )}
      </div>
      
      <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-purple-400 transition-colors">
        {video.title}
      </h3>
    </div>
  );
};

export default VODMyLibrary;
